import type { Document } from "@/api/models"

export interface SourceCardData {
  /** Stable id used for pinning state — library:file or url or tool+timestamp. */
  id: string
  /** Human-readable source name (filename, page title, etc.). */
  title: string
  /** First snippet the tool returned. */
  snippet?: string
  /** Broad bucket for color + icon. */
  type: "library" | "web" | "doc" | "m365" | "other"
  /** Name of the tool that retrieved this source. */
  tool: string
  /** ISO timestamp of retrieval. */
  timestamp: string
  /** Optional structured identifiers for citation matching. */
  library?: string
  sourcefile?: string
  sourcepage?: string
  url?: string
  /**
   * FILTH locators, present only on a card retrieved from a FILTH library.
   *
   * `filthCitationAlias` is the short id the deliverable cites the document by.
   * `filthContentHandle` is the opaque locator the content route accepts. The
   * panel needs both so it can open a citation through the delivered handle
   * rather than through whatever the model wrote.
   */
  filthContentHandle?: string
  filthCitationAlias?: string
  /** Matching strings the card "answers" to when the user hovers a citation. */
  matchKeys: string[]
  /** How many citations in the deliverable reference this source. */
  citationCount?: number
  /** Subagent that retrieved the source: orchestrator, scout, worker. */
  retrievedBy?: "deep_explorer" | "scout" | "worker" | string
  /** Short hex id (8 chars) for the specific subagent dispatch. */
  dispatchId?: string | null
}

/**
 * Content-route locator for a FILTH document. Persistence drops `sourcefile`
 * when it repeats the handle, and the live preview caps it at 300 characters,
 * so the ribbon click and the document list both rebuild from the handle.
 */
function filthPreviewSourcefile(handle: string): string {
  return `filth-citation/${handle}/preview.txt`
}

function savedDocumentHandle(document: Document): string {
  const handle = document.extra_data?.filth_content_handle
  return typeof handle === "string" ? handle : ""
}

function savedDocumentAlias(document: Document): string {
  const alias = document.extra_data?.filth_citation_alias
  return typeof alias === "string" ? alias : ""
}

/** doc_internal stores `document_id` as the tuple key. Library ids are not UUIDs. */
const DOCUMENT_ID_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isLibraryTupleKey(library: string): boolean {
  return !library.includes(":") && !DOCUMENT_ID_UUID.test(library)
}

/**
 * Identity shared by an activity card and the saved document for the same
 * retrieval. A FILTH row is the handle (the preview path is not stable).
 * An ordinary library row is `library` + `sourcefile`.
 */
function sourceCardMergeKey(card: SourceCardData): string | null {
  const handle = card.filthContentHandle?.trim()
  if (handle) return `handle:${handle}`
  const library = card.library?.trim()
  const sourcefile = card.sourcefile?.trim()
  if (library && sourcefile) return `file:${library}:${sourcefile}`
  return null
}

/**
 * Cards for saved message documents whose activity preview was dropped.
 *
 * The 256 KiB narrative cannot hold every unique handle. A FILTH document
 * keeps one ``filth_content_handle`` and may omit ``sourcefile``; the card
 * opens ``filth-citation/{handle}/preview.txt`` from that handle. An ordinary
 * library document has no handle: the card is `library` + `sourcefile` and
 * must not invent one.
 */
export function sourceCardsFromSavedDocuments(
  documents: [string, Document][] | undefined
): SourceCardData[] {
  const cards: SourceCardData[] = []
  for (const entry of documents ?? []) {
    const [library, document] = entry
    // `m365:file:…` and a document UUID are citation ids, not library ids.
    // FILTH rows and ordinary library rows (no colon, not a UUID) stay.
    if (!library || !isLibraryTupleKey(library)) continue
    const handle = savedDocumentHandle(document).trim()
    const sourcefile = document.sourcefile?.trim() || undefined
    const alias = savedDocumentAlias(document).trim()
    if (handle) {
      const locator = filthPreviewSourcefile(handle)
      const matchKeys = [
        alias ? `${library}:${alias}` : "",
        alias,
        `${library}:${locator}`,
        locator,
        sourcefile ? `${library}:${sourcefile}` : "",
        sourcefile ?? "",
      ].filter((key): key is string => Boolean(key))
      cards.push({
        id: `${library}:${handle}`,
        title: alias || document.original_sourcefile?.trim() || "Source",
        type: "library",
        tool: "library_search",
        timestamp: "",
        library,
        sourcefile,
        sourcepage: document.sourcepage ?? undefined,
        filthContentHandle: handle,
        filthCitationAlias: alias || undefined,
        matchKeys,
        citationCount: 1,
      })
      continue
    }
    if (!sourcefile) continue
    cards.push({
      id: `${library}:${sourcefile}`,
      title: document.original_sourcefile?.trim() || sourcefile,
      type: "library",
      tool: "library_search",
      timestamp: "",
      library,
      sourcefile,
      sourcepage: document.sourcepage ?? undefined,
      matchKeys: [`${library}:${sourcefile}`, sourcefile],
      citationCount: 1,
    })
  }
  return cards
}

/**
 * Arguments for the sidebar `openPreview` call so it receives the same
 * documents the inline viewer already resolved. Without them the content
 * URL is built from the citation text (`library:alias`) instead of the
 * delivered FILTH handle.
 */
export function sidebarPreviewArguments(
  citation: string,
  section: string,
  documents?: [string, Document][]
): {
  citation: string
  section: string
  options: { documents?: [string, Document][] }
} {
  return { citation, section, options: { documents } }
}

/**
 * Activity cards win when both describe the same document; saved docs fill
 * the gaps. Same document means the same FILTH handle, or the same
 * library file when there is no handle.
 */
export function mergeSourceCards(
  fromActivity: SourceCardData[],
  fromSavedDocuments: SourceCardData[]
): SourceCardData[] {
  const seen = new Set<string>()
  for (const card of fromActivity) {
    const key = sourceCardMergeKey(card)
    if (key) seen.add(key)
  }
  const extras: SourceCardData[] = []
  for (const card of fromSavedDocuments) {
    const key = sourceCardMergeKey(card)
    if (key) {
      if (seen.has(key)) continue
      seen.add(key)
    }
    extras.push(card)
  }
  return fromActivity.concat(extras)
}

/**
 * Rebuild the delivered-document list the citation resolver needs, from the
 * source cards the activity log produced.
 *
 * Deep Explorer never receives the chat response's `documents` channel, so
 * without this its citation resolver has nothing to match against and would
 * fall back to building a content URL out of the model's own citation text —
 * which for a FILTH document means asking the backend for a token it never
 * issued. The cards carry the two things that matter: the alias the
 * deliverable cites, and the handle the content route accepts.
 *
 * Every FILTH-backed card is returned, not just the cited one, so a citation
 * naming a document that was never retrieved is recognised as unresolvable
 * rather than silently turning into a request. Cards rebuilt from saved
 * message documents cover handles the 256 KiB activity narrative dropped.
 */
export function documentsFromSourceCards(sources: SourceCardData[]): [string, Document][] {
  const documents: [string, Document][] = []
  for (const source of sources) {
    const library = source.library?.trim()
    const handle = source.filthContentHandle?.trim()
    if (!library || !handle) continue
    documents.push([
      library,
      {
        id: null,
        content: null,
        embedding: null,
        category: null,
        sourcepage: source.sourcepage ?? null,
        // The card's own `sourcefile` is capped at 300 characters upstream, so
        // it cannot be trusted as a locator. Rebuild it from the uncapped
        // handle; a citation from an older session still matches this form.
        sourcefile: filthPreviewSourcefile(handle),
        oids: null,
        groups: null,
        captions: [],
        original_sourcefile: source.filthCitationAlias ?? source.title,
        extra_data: {
          filth_content_handle: handle,
          ...(source.filthCitationAlias ? { filth_citation_alias: source.filthCitationAlias } : {}),
        },
      },
    ])
  }
  return documents
}

/**
 * Match a citation's `[[...]]` inner string against a source's matchKeys.
 * Case-insensitive, substring-based. Pure helper exported for tests.
 */
export function citationMatchesSource(citationInner: string, matchKeys: string[]): boolean {
  if (!citationInner || matchKeys.length === 0) return false
  const normalized = citationInner.toLowerCase()
  return matchKeys.some((key) => key && normalized.includes(key.toLowerCase()))
}

/**
 * Reconstruct the citation string that `CitationPreviewProvider.openPreview`
 * expects from a SourceCardData row. Returns null when the source lacks
 * enough identifying info to resolve a document.
 */
export function sourceToCitation(source: SourceCardData): string | null {
  const pageNum = source.sourcepage ? parsePage(source.sourcepage) : null
  const page = pageNum != null ? `#page=${pageNum}` : ""
  switch (source.type) {
    case "library": {
      const handle = source.filthContentHandle?.trim()
      // Prefer the rebuilt handle path over a missing or truncated sourcefile.
      const sourcefile = handle ? filthPreviewSourcefile(handle) : source.sourcefile
      if (source.library && sourcefile) {
        return `${source.library}:${sourcefile}${page}`
      }
      return null
    }
    case "web":
      return source.url ?? null
    case "m365":
      return source.url ?? source.id
    case "doc":
    case "other":
    default: {
      if (source.url) return source.url
      if (source.sourcefile) return `${source.sourcefile}${page}`
      return null
    }
  }
}

/**
 * Source pages come from the backend as free-form strings ("3", "p. 3",
 * "3-5"). Parse the leading integer so the peek header and the viewer URL
 * agree; return null if we can't find one.
 */
export function parsePage(raw: string): number | null {
  const match = /\d+/.exec(raw)
  if (!match) return null
  const n = Number.parseInt(match[0], 10)
  return Number.isFinite(n) ? n : null
}

/**
 * Repair a deliverable citation that the model emitted without the required
 * `library_id:` prefix. The backend `/api/content/<section>/<path>` route
 * needs `<index_id>:<filename>` after the section, so a bare filename like
 * `R-012 Foo.pdf` produces a 400. When the activity log's source cards
 * contain a library source whose `sourcefile` matches the citation's
 * filename, prepend its `library` to reconstruct the canonical form.
 *
 * Citations that already have a colon (library:file, doc:id, m365:..., URLs)
 * are returned unchanged.
 */
export function resolveCitationWithSources(citation: string, sources: SourceCardData[]): string {
  if (!citation || sources.length === 0) return citation

  const pipeIdx = citation.indexOf("|")
  const sourceSegment = pipeIdx === -1 ? citation : citation.slice(0, pipeIdx)
  const tailSegment = pipeIdx === -1 ? "" : citation.slice(pipeIdx)

  const hashIdx = sourceSegment.indexOf("#")
  const baseSource = hashIdx === -1 ? sourceSegment : sourceSegment.slice(0, hashIdx)
  const hashFragment = hashIdx === -1 ? "" : sourceSegment.slice(hashIdx)

  // Anything with a colon (library:file, doc:id, m365:id, http(s)://…) is
  // either already canonical or not a library citation we can repair.
  if (baseSource.includes(":")) return citation

  // A FILTH card's `sourcefile` is the opaque locator, so a bare citation can
  // only ever match its alias — which is now the common case, since the alias
  // is what the model is given to cite.
  const match = sources.find(
    (s) =>
      s.type === "library" &&
      s.library &&
      (s.filthCitationAlias === baseSource || s.sourcefile === baseSource)
  )
  if (!match?.library) return citation

  return `${match.library}:${baseSource}${hashFragment}${tailSegment}`
}
